/*
    Copyright © DokiApp, 2025
*/

import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useStoreState } from '../../store/store.hooks'
import { useRelativeNavigate } from '../../hooks/useRelativeNavigate'
import { get } from '../../services/request'
import { Heading } from '../../components/Text'
import { NavigateBack } from '../../components/Nav/NavigateBack'
import { GradientButton, WhiteButton } from '../../components/Button'
import { MentalWellbeingDemographicsEmbed } from './MentalWellbeingDemographicsEmbed'

const SURVEY_KEY = 'who-10'

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  buttonContainer: {
    margin: '0 2rem',
  },
  buttonContainerWithPadding: {
    margin: '0 2rem 2rem',
  },
  centered: {
    textAlign: 'center',
  },
  container: {
    padding: '1rem',
  },
  greeting: {
    fontSize: '30px',
    marginBottom: '2rem',
    textAlign: 'center',
  },
  intro: {
    margin: '0 2rem',
    textAlign: 'justify',
  },
  description: {
    margin: '0 2rem 2rem',
    textAlign: 'justify',
  },
  consultationButton: {
    width: '90%',
    boxShadow: ' 0 10px 20px 0 rgba(100,115,175,0.4)',
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      marginBottom: 30,
    },
  },
  introText: {
    fontSize: '1.1rem',
  },
  page: {
    backgroundColor: '#fff',
    borderRadius: '15px',
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
  },
  previousResponsesButton: {
    width: '90%',
    boxShadow: ' 0 10px 20px 0 rgba(100,115,175,0.4)',
    border: '1px solid #6473AF',
  },
  startTestButton: {
    width: '90%',
    boxShadow: ' 0 10px 20px 0 rgba(100,115,175,0.4)',
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      marginBottom: 30,
    },
  },
  timeNeeded: {
    margin: '2rem',
  },
}))

const MentalWellbeingDiagnostics = () => {
  const classes = useStyles()
  const {
    t,
    i18n: { language },
  } = useTranslation('survey')
  const {
    profile: { profileContents },
  } = useStoreState((state) => state)
  const { relativeNavigate } = useRelativeNavigate()

  const [survey, setSurvey] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasDemographics, setHasDemographics] = useState(false)
  const [step, setStep] = useState('intro') // 'intro' | 'demographics' | 'missing'

  const handleBackToDashboard = () => {
    relativeNavigate('/')
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        const [surveyData, demographicsData] = await Promise.all([
          get(`/surveys/${SURVEY_KEY}?lng=${language}`),
          get('/surveys/mental-wellbeing/demographics'),
        ])

        setSurvey(surveyData)

        if (demographicsData && demographicsData.demographics) {
          setHasDemographics(true)
        }
      } catch (err) {
        console.error('Failed to fetch survey or demographics', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleStartTestClick = () => {
    if (hasDemographics) {
      relativeNavigate(`/surveys/${SURVEY_KEY}/questions`)
    } else {
      setStep('demographics')
    }
  }

  const handlePreviousResponsesClick = async () => {
    try {
      setLoading(true)
      const result = await get(
        `/surveys/mental-wellbeing-diagnostics/evaluation?lng=${language}`
      )
      if (result && result.hasResponses) {
        const redirectPathSegment =
          survey && (survey.category || survey.wellKnownKey)
        relativeNavigate(`/surveys/${redirectPathSegment}/evaluation`)
      } else {
        setStep('missing')
      }
    } catch (err) {
      console.error('Failed to fetch mental wellbeing evaluation', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDemographicsSaved = () => {
    setHasDemographics(true)
    relativeNavigate(`/surveys/${SURVEY_KEY}/questions`)
  }

  if (loading) {
    return (
      <Backdrop className={classes.backdrop} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  if (step === 'missing') {
    return (
      <Grid container className={classes.page}>
        <NavigateBack />
        <Grid item xs={12} className={classes.pageHeader}>
          <Heading>{t('missingResponsesHeader')}</Heading>
        </Grid>
        <Grid item xs={12} className={classes.description}>
          <Typography>{t('missingResponsesDescription')}</Typography>
        </Grid>
        <Grid
          container
          justifyContent="center"
          spacing={2}
          className={classes.buttonContainerWithPadding}
        >
          <Grid item xs={6} className={classes.centered}>
            <GradientButton
              className={classes.startTestButton}
              onClick={handleStartTestClick}
            >
              {t('missingResponsesStartNewTest')}
            </GradientButton>
          </Grid>
          <Grid item xs={6} className={classes.centered}>
            <GradientButton
              className={classes.consultationButton}
              onClick={handleBackToDashboard}
            >
              {t('mentalWellbeingEvalBackButton')}
            </GradientButton>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  if (step === 'demographics') {
    return (
      <Grid container className={classes.page}>
        <NavigateBack />
        <Grid item xs={12} className={classes.pageHeader}>
          <Heading>{t('mentalWellbeingDemographicsTitle')}</Heading>
        </Grid>

        <Grid item xs={12} className={classes.greeting}>
          <Typography variant="p">
            {t('greeting')} {profileContents?.name}!
          </Typography>
        </Grid>

        <Grid item xs={12} className={classes.intro}>
          <Typography className={classes.introText}>
            {t('mentalWellbeingDemographicsIntro')}
          </Typography>
        </Grid>

        <Grid item xs={12} className={classes.container}>
          <MentalWellbeingDemographicsEmbed onSave={handleDemographicsSaved} />
        </Grid>
      </Grid>
    )
  }

  // Step: intro (Page 1)
  return (
    <Grid container className={classes.page}>
      <NavigateBack />
      <Grid item xs={12} className={classes.pageHeader}>
        <Heading>{survey && survey.title}</Heading>
      </Grid>
      <Grid item xs={12} className={classes.greeting}>
        <Typography variant="p">
          {t('greeting')} {profileContents?.name}!
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.intro}>
        <Typography className={classes.introText}>
          {survey && survey.description}
        </Typography>
      </Grid>
      <Grid
        container
        justifyContent="center"
        spacing={2}
        className={classes.buttonContainer}
      >
        <Grid item xs={6} className={classes.centered}>
          <GradientButton
            className={classes.startTestButton}
            onClick={handleStartTestClick}
          >
            {t('startTest')}
          </GradientButton>
        </Grid>
        <Grid item xs={6} className={classes.centered}>
          <WhiteButton
            className={classes.previousResponsesButton}
            onClick={handlePreviousResponsesClick}
          >
            {t('previousResponses')}
          </WhiteButton>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.timeNeeded}>
        <Typography>{t('mentalWellbeingTimeNeeded')}</Typography>
      </Grid>
    </Grid>
  )
}

MentalWellbeingDiagnostics.displayName = 'MentalWellbeingDiagnostics'

export default MentalWellbeingDiagnostics
export { MentalWellbeingDiagnostics }
